import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home, ShieldAlert } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallbackTitle?: string;
  scope?: 'admin' | 'public' | 'global';
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[Application Crash caught by ErrorBoundary]:', error, errorInfo);
    this.setState({ error, errorInfo });
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleHardReload = () => {
    try {
      sessionStorage.clear();
      // Clear admin session/state caches if in admin scope
      const adminKeys = ['oltraya_admin_auth', 'oltraya_admin_mode'];
      adminKeys.forEach((k) => {
        localStorage.removeItem(k);
        sessionStorage.removeItem(k);
      });
    } catch {
      // Ignore
    }
    window.location.href = this.props.scope === 'admin' ? '/admin' : '/';
  };

  public render() {
    if (this.state.hasError) {
      const isChunkError =
        this.state.error?.message?.toLowerCase().includes('dynamically imported module') ||
        this.state.error?.message?.toLowerCase().includes('failed to fetch') ||
        this.state.error?.name === 'ChunkLoadError';

      return (
        <div className="min-h-screen bg-[#0d0f13] text-[#e8e6e3] flex items-center justify-center p-6 selection:bg-[#a88345]/30">
          <div className="max-w-xl w-full bg-[#16191f] border border-[#2a2f3a] rounded-lg p-8 shadow-2xl space-y-6 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-400 mx-auto">
              {this.props.scope === 'admin' ? (
                <ShieldAlert className="w-8 h-8" />
              ) : (
                <AlertTriangle className="w-8 h-8" />
              )}
            </div>

            <div className="space-y-2">
              <h2 className="text-xl md:text-2xl font-serif text-[#f4efe6]">
                {this.props.fallbackTitle ||
                  (this.props.scope === 'admin'
                    ? 'Admin Portal Encountered an Issue'
                    : 'Something went wrong')}
              </h2>
              <p className="text-sm text-[#9ca3af] leading-relaxed">
                {isChunkError
                  ? 'A new version of the site was deployed. Please reload the page to load the latest application assets.'
                  : (this.state.error?.message || 'An unexpected rendering error occurred. The application prevented a blank screen.')}
              </p>
            </div>

            {this.state.error?.stack && (
              <details className="text-left bg-[#0c0e12] border border-white/5 rounded p-3 text-xs font-mono text-[#717888] overflow-x-auto max-h-40">
                <summary className="cursor-pointer text-[#a88345] hover:underline mb-1">
                  Technical Diagnostics
                </summary>
                <div className="whitespace-pre-wrap">{this.state.error.stack}</div>
              </details>
            )}

            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
              <button
                onClick={this.handleReload}
                className="w-full sm:w-auto px-5 py-2.5 bg-[#a88345] hover:bg-[#b89150] text-[#0d0f13] font-semibold text-xs tracking-wider uppercase rounded transition-colors flex items-center justify-center gap-2 cursor-pointer"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                Reload Page
              </button>

              <button
                onClick={this.handleHardReload}
                className="w-full sm:w-auto px-5 py-2.5 bg-[#202530] hover:bg-[#2b3240] text-[#e8e6e3] font-medium text-xs tracking-wider uppercase border border-white/10 rounded transition-colors flex items-center justify-center gap-2 cursor-pointer"
              >
                <Home className="w-3.5 h-3.5" />
                {this.props.scope === 'admin' ? 'Reset Admin Session' : 'Back to Home'}
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
